import { withCustomConditionOption, withCustomOption } from '@components/form/v2';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconDropdown } from '@components/icons/dropdown';
import { Select } from '@components/select';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';
import { useOptionsFieldActions } from './use-field-actions';
import { useFieldOptions } from './use-field-options';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import {
  mappedDefaultSelectProps,
  SelectDefaultValueMapping,
  useMappedDefaultPill,
} from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { optionsTab } from '../options-tab';
import { RequiredSwitch } from '../required-switch';
import { StaleConditionWarningIcon } from '../stale-condition-warning';
import { ValueMenu } from '../variable-condition-menus';

type Config = FieldConfig<'input-dropdown'>;

const Display: Config['Display'] = ({ field }) => {
  const options = withCustomOption(field, useFieldOptions(field));
  const mappedPill = useMappedDefaultPill(field);
  const mappedSelect = mappedPill ? mappedDefaultSelectProps(mappedPill) : null;
  return (
    <Select
      placeholder={field.placeholder}
      value={mappedSelect ? mappedSelect.option : (field.defaultValue ?? null)}
      options={options}
      parseSingleValue={mappedSelect?.parseSingleValue}
      label={asteriskLabel(field.label, field.isRequired)}
      description={field.description}
      labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
      labelIconAfter={<StaleConditionWarningIcon field={field} />}
    />
  );
};

const FormBuilderDropdown: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;
  const menuActions = useOptionsFieldActions(field);
  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        menu={menuActions}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('placeholder')}
            />
            <RequiredSwitch field={field} />
            <SelectDefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Dropdown',
    label: 'Label',
    placeholder: 'Empty state text',
    default: 'Default option',
  },
});

export const dropdownConfig: Config = {
  kind: 'input-dropdown',
  title: t('title'),
  group: 'selections',
  propertiesTabs: [optionsTab, conditionsTab],
  Component: FormBuilderDropdown,
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconDropdown,
  condition: {
    comparators: ['is', 'not_is'],
    ValueMenu: ({ field, ...rest }) => {
      const options = withCustomConditionOption(field, useFieldOptions(field));
      return <ValueMenu options={options} {...rest} />;
    },
  },
};
